import React, { useState } from 'react';
import { Box, Stack, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { ActionButton, CancelButton } from '@/components/Button';
import { approvalService } from '@/services/approvalService';
import AddRemarkDialog from '@/modules/Approval/components/AddRemarkDialog';
import VerificationDialog from './VerificationDialog';
import VerificationResultsDialog from './VerificationResultsDialog';
import { rolePermissions, getUserRolesFromToken } from '@/config/routePermissions';
import { indigentService } from '@/services/indigentService';
import { verificationService, VerificationResponse } from '@/services/verificationService';
import { useResponsiveToast } from '@/components/Toast';
import { isIndigentVerificationEnabled } from '@/utils/featureUtils';

interface IndigentActionButtonsProps {
    requestId: string;
    status: string;
    onStatusUpdate?: () => void;
    indigentData?: {
        sa_id: string;
        name: string;
        surname: string;
        address1?: string;
        address2?: string;
        postalCode?: string;
    };
    indigentNumber?: string;
}

const IndigentActionButtons: React.FC<IndigentActionButtonsProps> = ({
    requestId,
    status,
    onStatusUpdate,
    indigentData,
    indigentNumber,
}) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    const [showExistingKycDialog, setShowExistingKycDialog] = useState(false);
    const [existingKycData, setExistingKycData] = useState<VerificationResponse | null>(null);
    const [dialogActionType, setDialogActionType] = useState<'approve' | 'reject' | 'pass' | 'fail'>('reject');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success, error: showError } = useResponsiveToast();

    // Get current user roles directly from JWT token for security
    const userRoles = getUserRolesFromToken();

    // Check if user can perform actions using centralized function
    const canPerformActions = rolePermissions.canPerformActions(userRoles);

    // Get current user ID from token (you might need to adjust this based on your token structure)
    const getCurrentUserId = (): string => {
        // This is a placeholder - you may need to adjust based on how user ID is stored in your token
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                return decoded.user_id || decoded.sub || 'unknown';
            } catch {
                return 'unknown';
            }
        }
        return 'unknown';
    };

    const handlePassDirect = async () => {
        if (!onStatusUpdate) return;

        setIsUpdating(true);
        try {
            await approvalService.updateApprovalStatus(requestId, 'reviewed');
            onStatusUpdate();
        } catch (error) {
            console.error('Error updating status to reviewed:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleFailDirect = async () => {
        if (!onStatusUpdate) return;

        setIsUpdating(true);
        try {
            await approvalService.updateApprovalStatus(requestId, 'failed');
            onStatusUpdate();
        } catch (error) {
            console.error('Error updating status to failed:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePass = () => {
        setDialogActionType('pass');
        setShowApprovalDialog(true);
    };


    const handleApproveClick = () => {
        setDialogActionType('approve');
        setShowApprovalDialog(true);
    };

    const handleRejectClick = () => {
        setDialogActionType('reject');
        setShowApprovalDialog(true);
    };

    const handleDialogConfirm = async (remark: string, file?: File) => {
        if (!onStatusUpdate) return;

        setIsUpdating(true);
        try {
            let status: 'reviewed' | 'approved' | 'failed' | 'rejected';
            switch (dialogActionType) {
                case 'pass':
                    status = 'reviewed';
                    break;
                case 'fail':
                    status = 'failed';
                    break;
                case 'approve':
                    status = 'approved';
                    break;
                case 'reject':
                    status = 'rejected';
                    break;
                default:
                    status = 'reviewed';
            }
            await approvalService.updateApprovalStatus(requestId, status, remark);
            setShowApprovalDialog(false);
            onStatusUpdate();
        } catch (error) {
            console.error(`Error updating status to ${dialogActionType}:`, error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleVerification = async () => {
        setIsUpdating(true);
        try {
            // First check if KYC data already exists
            const existingKyc = await verificationService.checkExistingKyc(requestId);
            
            if (existingKyc) {
                // KYC data exists, show results dialog with Force Update option
                setExistingKycData(existingKyc);
                setShowExistingKycDialog(true);
            } else {
                // No existing KYC data, open verification dialog
                setShowVerificationDialog(true);
            }
        } catch (error) {
            console.error('Error checking existing KYC data:', error);
            showError('Error checking verification data. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleVerificationDialogClose = () => {
        setShowVerificationDialog(false);
    };

    const handleExistingKycDialogClose = () => {
        setShowExistingKycDialog(false);
        setExistingKycData(null);
    };

    const handleForceUpdate = () => {
        // Close existing KYC dialog and open verification dialog for force update
        setShowExistingKycDialog(false);
        setShowVerificationDialog(true);
    };

    const handleCloseAllKycDialogs = () => {
        setShowExistingKycDialog(false);
        setExistingKycData(null);
    };

    const handleVerificationSubmit = async (data: { idNumber: string; firstName: string; surname: string }) => {
        if (!onStatusUpdate) return;

        setIsUpdating(true);
        try {
            // Create a minimal update data object with only the fields we want to update
            // We need to provide all required fields for the IndigentFormData interface
            const updateData = {
                ward_no: '', // Will be ignored by backend if empty
                electricity_meter_no: '', // Will be ignored by backend if empty
                erf_no: '', // Will be ignored by backend if empty
                service_electricity: false, // Default value
                service_waste_water: false, // Default value
                service_water: false, // Default value
                service_waste: false, // Default value
                sa_id: data.idNumber, // The field we want to update
                name: data.firstName, // The field we want to update
                surname: data.surname, // The field we want to update
                town: '', // Will be ignored by backend if empty
                occupants: [], // Empty array
                financial_year: '', // Will be ignored by backend if empty
                document_types: '', // Will be ignored by backend if empty
                account_number: '', // Will be ignored by backend if empty
                declaration_accepted: false, // Default value
                signature_file: new File([''], 'empty.txt'), // Empty file
                occupant_photos: [], // Empty array
                files: [], // Empty array
            };

            await indigentService.updateIndigent(requestId, updateData);
            
            // Trigger status update to refresh the data
            onStatusUpdate();
            
            success('Indigent details verified and updated successfully!');
        } catch (error) {
            console.error('Error verifying indigent details:', error);
            showError('Error verifying indigent details. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDialogCancel = () => {
        setShowApprovalDialog(false);
    };

    // Map API status to UI status for button display logic
    const getUIStatus = (apiStatus: string): string => {
        switch (apiStatus) {
            case 'pending':
                return 'Pending';
            case 'reviewed':
                return 'Review Passed';
            case 'approved':
                return 'Approved';
            case 'failed':
                return 'Review Failed';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Pending';
        }
    };

    const uiStatus = getUIStatus(status);

    // Don't show buttons if user cannot perform actions
    if (!canPerformActions) {
        return null;
    }

    // When Approved/Rejected and verification is disabled per tenant, show no buttons
    const verificationEnabled = isIndigentVerificationEnabled();
    if ((uiStatus === 'Approved' || uiStatus === 'Rejected') && !verificationEnabled) {
        return null;
    }

    // Check if only verification button will be shown (Approved or Rejected status)
    const onlyVerificationButton = (uiStatus === 'Approved' || uiStatus === 'Rejected') && verificationEnabled;

    return (
        <>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 3,
                mb: 2 
            }}>
                <Stack 
                    direction={isMobile ? 'column' : 'row'} 
                    spacing={2}
                    sx={{ 
                        width: '100%', 
                        maxWidth: onlyVerificationButton ? '250px' : '480px' 
                    }}
                >
                    {/* Show Pass/Fail buttons for pending status */}
                    {uiStatus === 'Pending' && (
                        <>
                            <ActionButton
                                onClick={handlePassDirect}
                                disabled={isUpdating}
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#0f254d' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Pass Review'
                                )}
                            </ActionButton>
                            <CancelButton
                                onClick={handleFailDirect}
                                disabled={isUpdating}
                                sx={{
                                    color: '#DE3B40',
                                    borderColor: '#DE3B40',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#FFECEC' },
                                    '&:disabled': {
                                        color: '#ccc',
                                        borderColor: '#ccc',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Fail Review'
                                )}
                            </CancelButton>
                        </>
                    )}

                    {/* Show Approve/Reject buttons for Review Passed status */}
                    {uiStatus === 'Review Passed' && (
                        <>
                            <ActionButton
                                onClick={handleApproveClick}
                                disabled={isUpdating}
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#0f254d' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Approve'
                                )}
                            </ActionButton>
                            <CancelButton
                                onClick={handleRejectClick}
                                disabled={isUpdating}
                                sx={{
                                    color: '#DE3B40',
                                    borderColor: '#DE3B40',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#FFECEC' },
                                    '&:disabled': {
                                        color: '#ccc',
                                        borderColor: '#ccc',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Reject'
                                )}
                            </CancelButton>
                        </>
                    )}

                    {/* Show Pass/Reject buttons for Review Failed status */}
                    {uiStatus === 'Review Failed' && (
                        <>
                            <ActionButton
                                onClick={handlePass}
                                disabled={isUpdating}
                                sx={{
                                    backgroundColor: '#132F5F',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#0f254d' },
                                    '&:disabled': { backgroundColor: '#ccc' },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Pass'
                                )}
                            </ActionButton>
                            <CancelButton
                                onClick={handleRejectClick}
                                disabled={isUpdating}
                                sx={{
                                    color: '#DE3B40',
                                    borderColor: '#DE3B40',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: { xs: 14, sm: 16 },
                                    height: { xs: 48, sm: 56 },
                                    width: '100%',
                                    '&:hover': { backgroundColor: '#FFECEC' },
                                    '&:disabled': {
                                        color: '#ccc',
                                        borderColor: '#ccc',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {isUpdating ? (
                                    <CircularProgress size={24} color='inherit' />
                                ) : (
                                    'Reject'
                                )}
                            </CancelButton>
                        </>
                    )}

                    {/* Verification button - available for all statuses, only when enabled for tenant */}
                    {verificationEnabled && (
                        <ActionButton
                            onClick={handleVerification}
                            disabled={isUpdating}
                            sx={{
                                backgroundColor: '#132F5F',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: { xs: 14, sm: 16 },
                                height: { xs: 48, sm: 56 },
                                width: '100%',
                                '&:hover': { backgroundColor: '#0f254d' },
                                '&:disabled': { backgroundColor: '#ccc' },
                            }}
                        >
                            {isUpdating ? (
                                <CircularProgress size={24} color='inherit' />
                            ) : (
                                'Verification'
                            )}
                        </ActionButton>
                    )}
                </Stack>
            </Box>

            <AddRemarkDialog
                open={showApprovalDialog}
                onClose={handleDialogCancel}
                onConfirm={handleDialogConfirm}
                actionType={dialogActionType}
                isLoading={isUpdating}
            />

            {/* Verification Dialog - only mount when feature is enabled for tenant */}
            {verificationEnabled && indigentData && indigentNumber && (
                <VerificationDialog
                    open={showVerificationDialog}
                    onClose={handleVerificationDialogClose}
                    onVerify={handleVerificationSubmit}
                    initialData={{
                        idNumber: indigentData.sa_id,
                        firstName: indigentData.name,
                        surname: indigentData.surname,
                        addressLine1: indigentData.address1 || '',
                        addressLine2: indigentData.address2,
                        postalCode: '', // User needs to enter manually
                    }}
                    requestId={requestId} // Pass the form/record ID
                    indigentNumber={indigentNumber}
                    userId={getCurrentUserId()}
                    isVerifying={isUpdating}
                />
            )}

            {/* Existing KYC Results Dialog */}
            {verificationEnabled && (
                <VerificationResultsDialog
                    open={showExistingKycDialog}
                    onClose={handleExistingKycDialogClose}
                    onCloseAll={handleCloseAllKycDialogs}
                    verificationData={existingKycData}
                    showForceUpdate={true}
                    onForceUpdate={handleForceUpdate}
                />
            )}
        </>
    );
};

export default IndigentActionButtons;
